/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Target, Pencil, Trash2, Plus, ArrowRight } from "lucide-react";
import KPIEditorDialog from "@/components/KPIEditorDialog";
import KPITemplatePicker from "@/components/KPITemplatePicker";
import ImportResultsDialog from "@/components/ImportResultsDialog";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { isPaidStarter } from "@/lib/effectivePlan";

// =====================================================================
// Phase 2Z.s — Performance Focus Lite
// =====================================================================
// Manager view that lets companies track activity results per
// employee, compare to targets, and link each activity to a rota
// category. Re-uses the existing Phase 1E KPI backend (kpi_definitions
// + kpi_values) with two additive fields: `linked_category_id` and
// `target_basis`. NEVER auto-applies; the "Suggest rota focus"
// button deep-links to RotaStylePage with a `?rmi_command=...`
// query string, where the existing RMI / Coverage Guard / preview /
// Apply chain remains the source of truth.
//
// Templates that are absence/overtime/weekend-shaped are hidden from
// this page (they remain available in the legacy /app/kpis page).
// =====================================================================

// Templates intentionally hidden on the Performance Focus page.
// They exist in the legacy KPITemplatePicker catalogue but feel
// out of place here (this is a performance/activity page, not an
// HR absence page).
const PERFORMANCE_HIDDEN_TEMPLATE_KEYS = new Set([
  "absence_pct", "overtime_hours", "weekend_balance",
]);

// Pure helper — bucket an achievement percentage into 1-5 dots.
// Exported through the module so source-level locks can lock it.
export function _performanceAchievementDots(pct) {
  if (pct == null || !isFinite(pct)) return 0;
  if (pct < 60) return 1;
  if (pct < 80) return 2;
  if (pct < 100) return 3;
  if (pct < 120) return 4;
  return 5;
}

// ===== Phase 2Z.u — Performance Focus Dashboard Lite helpers =====
// Pure, deterministic helpers used by the new Dashboard tab.
// Kept module-scope so source-level locks + Node behavioural tests
// can pin their contract. NEVER touch state, network or DOM.

export function _dashboardClassifyStatus(pct) {
  if (pct == null || !isFinite(pct)) return "no_data";
  if (pct < 60) return "red";
  if (pct < 80) return "amber";
  return "green";
}

export function _dashboardComputeDelta(current, previous) {
  if (current == null || previous == null
      || !isFinite(current) || !isFinite(previous)) return null;
  return Math.round((current - previous) * 10) / 10;
}

// ===== Phase 2Z.u-2 — Availability-fairness helpers =====
// Pure / deterministic / read-only. Used by BOTH the Dashboard tab
// and the Report tab to scale the *expected* target so an employee
// on leave isn't unfairly compared against a full-week target.
// Mirrors the Python helper `_performance_availability_ratio`.

export function _performanceAvailabilityRatio(
  shiftMinutes, leaveMinutes, bankHolidayMinutes
) {
  const shift = Number(shiftMinutes) || 0;
  const leave = Number(leaveMinutes) || 0;
  const bh = Number(bankHolidayMinutes) || 0;
  // Nothing to adjust → caller keeps the base expected.
  if (leave + bh <= 0) return null;
  // Leave booked but no scheduled work → fully unavailable.
  if (shift <= 0) return 0.0;
  const avail = Math.max(0, shift - leave - bh);
  const ratio = avail / shift;
  if (ratio < 0) return 0.0;
  if (ratio > 1) return 1.0;
  return ratio;
}

// Apply an availability ratio to a base expected target.
//  • null base       → null (no target set yet)
//  • null ratio      → unchanged base (no adjustment applies)
//  • 0 ratio         → null ("no available working time")
//  • otherwise       → base × ratio
export function _performanceAdjustedExpected(baseExpected, availabilityRatio) {
  if (baseExpected == null || !isFinite(baseExpected)) return null;
  if (availabilityRatio == null) return baseExpected;
  if (availabilityRatio <= 0) return null;
  return baseExpected * Math.min(1.0, Math.max(0.0, availabilityRatio));
}
// ===== _dashboardHelpers end =====

// Phase 2Z.s-2/4 — Pure helper. How many target-frequency units
// fit in a date window. Working-days-aware: only days that fall on
// one of the activity's `targetDays` (weekday indices, Monday=0)
// count. Default Mon-Fri so legacy activities behave sensibly.
// Mirrors the Python helper `_performance_units_in_window`.
const PERFORMANCE_DEFAULT_TARGET_DAYS = [1, 2, 3, 4, 5];  // JS: Mon=1..Fri=5

export function _performanceUnitsInWindow(
  frequency, fromDateIso, toDateIso, targetDays
) {
  if (!frequency || !fromDateIso || !toDateIso) return 0;
  const a = new Date(fromDateIso + "T00:00:00Z");
  const b = new Date(toDateIso + "T00:00:00Z");
  if (isNaN(a) || isNaN(b) || a > b) return 0;
  // Python convention is Monday=0 .. Sunday=6. JS Date.getUTCDay()
  // is Sunday=0 .. Saturday=6. Normalise inbound `targetDays`
  // (Python) to the JS convention.
  const pyToJs = (d) => ((Number(d) + 1) % 7);
  const daysSet = new Set(
    (Array.isArray(targetDays) && targetDays.length > 0
      ? targetDays.map(pyToJs)
      : PERFORMANCE_DEFAULT_TARGET_DAYS)
  );
  const daysPerWeek = daysSet.size;
  if (daysPerWeek === 0) return 0;
  const span = Math.round((b - a) / 86400000) + 1;
  let working = 0;
  for (let i = 0; i < span; i += 1) {
    const d = new Date(a.getTime() + i * 86400000);
    if (daysSet.has(d.getUTCDay())) working += 1;
  }
  if (frequency === "per_day") return working;
  if (frequency === "per_week") return working / daysPerWeek;
  if (frequency === "per_month") {
    const meanPerMonth = daysPerWeek * 30.4375 / 7;
    return meanPerMonth > 0 ? working / meanPerMonth : 0;
  }
  return 0;
}

// Pure helper — expected target for a window, used when we don't
// have a backend snapshot yet (e.g. before the first save). When
// reading a saved value, prefer the snapshot on the value doc.
export function _performanceExpectedForWindow(
  targetValue, frequency, fromDateIso, toDateIso, targetDays
) {
  if (targetValue == null || !isFinite(targetValue)) return null;
  const units = _performanceUnitsInWindow(
    frequency, fromDateIso, toDateIso, targetDays);
  if (units <= 0) return null;
  return Number(targetValue) * units;
}

// Phase 2Z.s-3 — Pure helper. Given a saved value's period and
// the currently-selected window, classify it as:
//   "exact"      — boundaries match (the grid loads the value)
//   "overlap"    — partial intersection (banner only — grid stays empty)
//   null         — no overlap (ignored)
// Both periods are inclusive ISO date strings.
export function _performanceClassifyOverlap(
  savedStart, savedEnd, windowStart, windowEnd
) {
  if (!savedStart || !savedEnd || !windowStart || !windowEnd) return null;
  if (savedStart === windowStart && savedEnd === windowEnd) return "exact";
  // Half-open interval overlap test (inclusive on both ends).
  if (savedStart <= windowEnd && savedEnd >= windowStart) return "overlap";
  return null;
}

// Pure helper — compute the rota-focus RMI command for the
// "Suggest rota focus" deep-link. Single safe sentence; never
// auto-applies anything.
export function _performanceBuildRmiCommand(activity) {
  if (!activity) return "";
  const cat = activity.linked_category_name || activity.name || "this activity";
  return (
    `${cat} is under target. Consider increasing ${cat} `
    + "using the highest-share available category. Preview before applying."
  );
}

// Pure helper — compute target × basis multiplier for the
// roll-up. Per-employee multiplies by employee count; team /
// company use the raw target value once.
export function _performanceTargetFor(target, basis, employeeCount) {
  if (target == null || !isFinite(target)) return null;
  if (basis === "team" || basis === "company") return target;
  // Default and "per_employee".
  return target * (employeeCount || 0);
}


export default function PerformanceFocusPage() {
  // Phase 2Z.aa - paid Starter customers see a locked-state card.
  // Wrapping keeps the inner component's hooks untouched.
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/billing/me").then(r => setMe(r.data)).catch(() => setMe({}));
  }, []);
  if (me && isPaidStarter(me)) {
    return (
      <LockedFeatureCard
        testid="performance-focus-locked-starter"
        badge="GROWTH"
        title="Performance Focus is available on Growth and Command"
        body="Compare activity results against rota-assigned work and see where your team needs more focus. On Starter, you can still use core rota tools and basic reports."
        primary={{ to: "/app/billing", label: "Upgrade to Growth" }}
        secondary={{ to: "/app/roster", label: "Open Roster" }}
      />
    );
  }
  if (!me) return null;
  return <PerformanceFocusPageInner />;
}

function PerformanceFocusPageInner() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("activities");

  // ----- Shared state -----
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editor, setEditor] = useState({ open: false, editing: null });
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  // Phase 2Z.s-5 — Import dialog state. Activity locked when opened.
  const [importDialog, setImportDialog] = useState({ open: false, activity: null });

  // Phase 2Z.u — Dashboard tab state. Pure composition over the new
  // GET /api/performance-focus/dashboard endpoint. No writes.
  const [dashScope, setDashScope] = useState("company");
  const [dashScopeId, setDashScopeId] = useState("");
  const [dashTeams, setDashTeams] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  // Grid + report state.
  const today = new Date().toISOString().slice(0, 10);
  const sevenAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  })();
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [fromDate, setFromDate] = useState(sevenAgo);
  const [toDate, setToDate] = useState(today);
  // actuals keyed by employee_id → { current: value | null, saving: bool, value_id: str|null }
  const [actuals, setActuals] = useState({});
  const [savedHash, setSavedHash] = useState({});
  const [assignedByEmp, setAssignedByEmp] = useState({});  // emp_id → minutes on linked cat
  // Phase 2Z.u-2 — per-employee availability for the selected window.
  // Pulled from /reports/company alongside `assignedByEmp` so the
  // Report tab can adjust the per-row expected target by leave/BH.
  // Shape: { [emp_id]: { shift_minutes, leave_minutes, bank_holiday_minutes } }
  const [availabilityByEmp, setAvailabilityByEmp] = useState({});
  // Phase 2Z.s-3 — overlapping-saved-period detector. When the
  // selected window partially overlaps an existing saved range,
  // the grid would otherwise look empty even though saved results
  // do exist nearby. We surface a friendly banner instead.
  // Shape: { [emp_id]: [{ value, period_start, period_end }] }
  const [overlappingByEmp, setOverlappingByEmp] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  // ----- Initial load -----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: kpis }, { data: cats }, { data: emps }] = await Promise.all([
        api.get("/kpis?active_only=true"),
        api.get("/categories"),
        api.get("/employees"),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setEmployees(Array.isArray(emps) ? emps : []);
      // Performance Focus surfaces ALL activities tagged with
      // "performance_focus" (created via this page or its template
      // picker), regardless of whether the target/link has been
      // filled in yet. We also include legacy activities that
      // have BOTH a linked category AND a target value, so
      // pre-existing setups are not lost. Each row will show a
      // clear "Needs setup" hint when fields are missing.
      const perfList = (Array.isArray(kpis) ? kpis : []).filter((k) => {
        const tagged = Array.isArray(k.tags) && k.tags.includes("performance_focus");
        const legacy = k.linked_category_id && k.target_value != null;
        return tagged || legacy;
      });
      setActivities(perfList);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ----- Activity name lookup with linked category name baked in -----
  const activitiesEnriched = useMemo(() => {
    const byId = Object.fromEntries(categories.map((c) => [c.category_id, c.name]));
    return activities.map((a) => ({
      ...a,
      linked_category_name: byId[a.linked_category_id] || null,
    }));
  }, [activities, categories]);

  const selectedActivity = useMemo(
    () => activitiesEnriched.find((a) => a.kpi_id === selectedActivityId) || null,
    [activitiesEnriched, selectedActivityId]
  );

  // Phase 2Z.an.2 — restrict the visible employee list per
  // selected activity. Selected-teams-only activities should NOT
  // surface employees from non-selected teams in Enter results
  // or Report (those rows can't have results recorded against
  // them anyway and would just confuse the manager). When the
  // backend has not yet returned `applicable_team_ids` (or the
  // activity is in All-teams mode), this collapses to the full
  // company list — i.e. behaviour is byte-stable for legacy
  // activities.
  const effectiveEmployees = useMemo(() => {
    if (!selectedActivity) return employees;
    const allowed = selectedActivity.applicable_team_ids;
    const isSelectedMode =
      selectedActivity.target_value == null
      && Array.isArray(allowed) && allowed.length > 0;
    if (!isSelectedMode) return employees;
    const set = new Set(allowed);
    return employees.filter((e) => set.has(e.team_id));
  }, [employees, selectedActivity]);

  // ----- Load actuals + assigned-hours when the grid tab opens -----
  const loadGrid = useCallback(async () => {
    if (!selectedActivity || !fromDate || !toDate) {
      setActuals({}); setAssignedByEmp({}); setOverlappingByEmp({}); return;
    }
    setReportLoading(true);
    try {
      // Phase 2Z.s-3 — widen the fetch by 92 days on each side so
      // we also surface saved values that START before the
      // selected window (the backend filters by period_start
      // only). 92 days catches any reasonable per-month frequency
      // overlap without unbounded growth.
      const _shift = (iso, days) => {
        const d = new Date(iso + "T00:00:00Z");
        d.setUTCDate(d.getUTCDate() + days);
        return d.toISOString().slice(0, 10);
      };
      const wideFrom = _shift(fromDate, -92);
      const wideTo = _shift(toDate, 92);
      const { data: vals } = await api.get(
        `/kpis/${selectedActivity.kpi_id}/values?`
        + `from=${wideFrom}&to=${wideTo}`
      );
      // Bucket each value as "exact" (boundaries match) or
      // "overlap" (partial intersection). Only the "exact" value
      // populates the grid input; "overlap" values feed the
      // friendly banner.
      const byEmpExact = {};
      const byEmpOverlap = {};
      (Array.isArray(vals) ? vals : []).forEach((v) => {
        const eid = v.scope_id || null;
        if (!eid) return;
        const cls = _performanceClassifyOverlap(
          v.period_start, v.period_end, fromDate, toDate);
        if (cls === "exact") {
          const prev = byEmpExact[eid];
          if (!prev || (v.recorded_at || "") > (prev.recorded_at || "")) {
            byEmpExact[eid] = v;
          }
        } else if (cls === "overlap") {
          (byEmpOverlap[eid] = byEmpOverlap[eid] || []).push({
            value: v.value,
            period_start: v.period_start,
            period_end: v.period_end,
            value_id: v.value_id,
          });
        }
      });
      const next = {};
      employees.forEach((e) => {
        const v = byEmpExact[e.employee_id];
        next[e.employee_id] = {
          current: v?.value ?? null,
          value_id: v?.value_id ?? null,
          // Phase 2Z.s-2 — keep the per-value snapshot so the
          // report column for THIS row uses the target that
          // applied when the result was saved, even after the
          // manager edits the activity target later.
          target_snapshot: v?.target_snapshot ?? null,
        };
      });
      setActuals(next);
      setSavedHash(JSON.stringify(next));
      setOverlappingByEmp(byEmpOverlap);

      // Pull existing utilisation report — gives us assigned minutes
      // per employee per category for the same window. Read-only.
      const { data: rep } = await api.get(
        `/reports/company?from_date=${fromDate}&to_date=${toDate}`
      );
      const linkedId = selectedActivity.linked_category_id;
      const aMap = {};
      const availMap = {};
      (rep.employees || []).forEach((e) => {
        const m = (e.by_category || {})[linkedId] || 0;
        aMap[e.employee_id] = m;
        // Phase 2Z.u-2 — capture availability so the per-row expected
        // can be reduced by leave / BH minutes in this window.
        availMap[e.employee_id] = {
          shift_minutes: e.shift_minutes || 0,
          leave_minutes: e.leave_minutes || 0,
          bank_holiday_minutes: e.bank_holiday_minutes || 0,
        };
      });
      setAssignedByEmp(aMap);
      setAvailabilityByEmp(availMap);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load grid");
    } finally {
      setReportLoading(false);
    }
  }, [selectedActivity, fromDate, toDate, employees]);

  useEffect(() => {
    if (tab === "enter" || tab === "report") loadGrid();
  }, [tab, loadGrid]);

  const isDirty = useMemo(
    () => JSON.stringify(actuals) !== savedHash,
    [actuals, savedHash]
  );

  // ----- Save bulk actuals (with replace-on-conflict flow) -----
  const saveAll = async () => {
    if (!selectedActivity) return;
    let changed = 0;
    let failed = 0;
    // Collect conflicts so we can surface ONE confirm dialog at
    // the end instead of N nested prompts.
    const conflicts = [];
    for (const eid of Object.keys(actuals)) {
      const row = actuals[eid];
      if (row.current == null || row.current === "") continue;
      const numeric = Number(row.current);
      if (!isFinite(numeric)) continue;
      try {
        await api.post(`/kpis/${selectedActivity.kpi_id}/values`, {
          period: "custom",
          period_start: fromDate,
          period_end: toDate,
          value: numeric,
          source: "manual",
          scope_id: eid,
        });
        changed += 1;
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 409 && detail && typeof detail === "object") {
          conflicts.push({ eid, numeric, detail });
        } else {
          failed += 1;
        }
      }
    }
    // Surface conflicts as a single friendly confirm.
    if (conflicts.length > 0) {
      const empName = (id) => (employees.find((e) => e.employee_id === id)?.name) || id;
      const msg = conflicts.length === 1
        ? `${empName(conflicts[0].eid)} already has a result that overlaps `
          + `this window (${conflicts[0].detail.existing_period_start} to `
          + `${conflicts[0].detail.existing_period_end}). Replace it?`
        : `${conflicts.length} employees already have overlapping results `
          + "for this window. Replace all of them?";
      const ok = window.confirm(msg);
      if (ok) {
        for (const c of conflicts) {
          try {
            await api.post(
              `/kpis/${selectedActivity.kpi_id}/values?replace=true`, {
                period: "custom",
                period_start: fromDate,
                period_end: toDate,
                value: c.numeric,
                source: "manual",
                scope_id: c.eid,
              });
            changed += 1;
          } catch (err) {
            failed += 1;
          }
        }
      }
    }
    if (failed) {
      toast.error(`Saved ${changed} — ${failed} failed.`);
    } else if (changed > 0) {
      toast.success(`Saved ${changed} result(s).`);
    } else if (conflicts.length > 0) {
      toast.info("No changes saved.");
    }
    await loadGrid();
  };

  // ----- Suggest rota focus → deep-link RotaStylePage -----
  const suggestRotaFocus = (activity) => {
    const cmd = _performanceBuildRmiCommand(activity);
    navigate(`/app/rota-style?rmi_command=${encodeURIComponent(cmd)}`);
  };

  // ----- UI helpers -----
  const fmtPct = (n) => (n == null ? "—" : `${Math.round(n)}%`);
  const fmtHours = (m) => (m == null ? "—" : `${(m / 60).toFixed(1)} h`);
  const dots = (n) =>
    [1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        className={
          "inline-block w-2 h-2 mx-[1px] rounded-full "
          + (i <= n ? "bg-[#0F1B3D]" : "bg-[#D9DEE8]")
        }
      />
    ));

  // ----- Computed report rows -----
  const reportRows = useMemo(() => {
    if (!selectedActivity) return [];
    const targetValue = selectedActivity.target_value;
    const frequency = selectedActivity.target_frequency || "per_week";
    const targetDays = selectedActivity.target_days;
    const liveExpected = _performanceExpectedForWindow(
      targetValue, frequency, fromDate, toDate, targetDays);
    // Phase 2Z.an.2 — for Selected-teams-only activities, compute
    // a per-employee live expected from their team's override (the
    // base target_value is intentionally null in that mode).
    const teamOverrideMap = {};
    (selectedActivity.team_overrides || []).forEach((ov) => {
      teamOverrideMap[ov.team_id] = ov;
    });
    const isSelectedMode =
      targetValue == null
      && (selectedActivity.applicable_team_ids || []).length > 0;
    return effectiveEmployees.map((e) => {
      const row = actuals[e.employee_id] || { current: null };
      const actual = row.current;
      const actualNum = actual == null || actual === "" ? null : Number(actual);
      // Phase 2Z.s-4 — historical safety AND sensible fallback.
      // Prefer the snapshot only when it carries a VALID positive
      // expected_for_window (the activity had a target + frequency
      // when the result was saved). When the snapshot is missing,
      // zero, or invalid, fall back to the current live expected
      // so the report never shows "0 snap". We NEVER overwrite a
      // valid snapshot — the snapshot row will still surface its
      // own value when present and positive.
      const snap = row.target_snapshot;
      const snapVal = snap && snap.expected_for_window;
      const snapValid = (typeof snapVal === "number" && isFinite(snapVal) && snapVal > 0);
      // Phase 2Z.an.2 — Selected-teams-only fallback: when no
      // valid snapshot, derive the row's expected from the
      // employee's team override.
      let liveExpectedForRow = liveExpected;
      if (isSelectedMode) {
        const ov = teamOverrideMap[e.team_id];
        if (ov && ov.target_value != null) {
          liveExpectedForRow = _performanceExpectedForWindow(
            ov.target_value,
            ov.target_frequency || frequency,
            fromDate, toDate,
            (ov.target_days && ov.target_days.length)
              ? ov.target_days
              : targetDays);
        } else {
          liveExpectedForRow = null;
        }
      }
      const baseExpected = snapValid ? snapVal : liveExpectedForRow;
      // Phase 2Z.u-2 — availability adjustment. Read-only over the
      // saved snapshot. When the employee had leave/BH in the
      // window, scale the expected target by their availability
      // ratio so they aren't unfairly judged against a full-week
      // target. Actuals stay untouched.
      const avail = availabilityByEmp[e.employee_id];
      const availRatio = avail
        ? _performanceAvailabilityRatio(
            avail.shift_minutes, avail.leave_minutes,
            avail.bank_holiday_minutes)
        : null;
      const t = _performanceAdjustedExpected(baseExpected, availRatio);
      const leaveMins = (avail?.leave_minutes || 0)
        + (avail?.bank_holiday_minutes || 0);
      const adjustedByLeave = availRatio !== null && availRatio < 1;
      const fullyUnavailable = availRatio === 0;
      const gap = actualNum != null && t != null ? actualNum - t : null;
      const ach = actualNum != null && t ? (actualNum / t) * 100 : null;
      return {
        employee_id: e.employee_id,
        name: e.name,
        assigned_mins: assignedByEmp[e.employee_id] || 0,
        target: t,
        actual: actualNum,
        gap,
        achievement_pct: ach,
        score: _performanceAchievementDots(ach),
        // Mark snapshot-based rows so the UI can surface a subtle
        // tooltip (no visible "snap" badge any more).
        from_snapshot: snapValid,
        // Phase 2Z.u-2 — availability adjustment metadata for UI.
        adjusted_by_leave: adjustedByLeave,
        fully_unavailable: fullyUnavailable,
        leave_minutes_in_window: leaveMins,
      };
    });
  }, [effectiveEmployees, actuals, assignedByEmp, availabilityByEmp,
       selectedActivity, fromDate, toDate]);

  const teamRollup = useMemo(() => {
    if (!selectedActivity) return null;
    // Team expected = sum of per-row expected (which honours each
    // row's snapshot when present). This guarantees rollups stay
    // consistent with the per-row numbers even when the activity
    // target changed between rows.
    const teamTarget = reportRows.reduce(
      (acc, r) => acc + (r.target != null ? r.target : 0), 0);
    const actuals_sum = reportRows.reduce(
      (acc, r) => acc + (r.actual != null ? r.actual : 0), 0
    );
    const gap = teamTarget ? actuals_sum - teamTarget : null;
    const ach = teamTarget ? (actuals_sum / teamTarget) * 100 : null;
    const assigned_mins = reportRows.reduce(
      (acc, r) => acc + (r.assigned_mins || 0), 0
    );
    return {
      assigned_mins,
      target: teamTarget || null,
      actual: actuals_sum,
      gap,
      achievement_pct: ach,
      under_target: ach != null && ach < 100,
    };
  }, [reportRows, selectedActivity]);

  // Phase 2Z.u — Dashboard fetch + teams load.
  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const params = new URLSearchParams({
        scope: dashScope, from_date: fromDate, to_date: toDate,
      });
      if (dashScope !== "company" && dashScopeId) {
        params.set("scope_id", dashScopeId);
      }
      const { data } = await api.get(
        `/performance-focus/dashboard?${params.toString()}`);
      setDashData(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail)
        || "Failed to load dashboard");
      setDashData(null);
    } finally { setDashLoading(false); }
  }, [dashScope, dashScopeId, fromDate, toDate]);

  useEffect(() => {
    // Phase 2Z.am — teams are needed by the activity editor's Team
    // overrides section AND the dashboard scope picker, so load
    // them once on mount instead of waiting for the dashboard tab.
    if (dashTeams.length === 0) {
      api.get("/teams")
        .then((r) => setDashTeams(r.data || []))
        .catch(() => setDashTeams([]));
    }
  }, [dashTeams.length]);

  useEffect(() => {
    if (tab !== "dashboard") return;
    // Don't fire until the scope_id is selected for non-company scopes.
    if (dashScope !== "company" && !dashScopeId) {
      setDashData(null); return;
    }
    loadDashboard();
  }, [tab, dashScope, dashScopeId, fromDate, toDate, loadDashboard]);

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <div className="space-y-4" data-testid="performance-focus-page">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-[#EEF2FF] p-3">
          <Target className="w-5 h-5 text-[#0F1B3D]" />
        </div>
        <div className="flex-1">
          <h1
            className="text-2xl font-semibold text-[#0F1B3D]"
            data-testid="performance-focus-title"
          >
            Performance Focus
          </h1>
          <p className="text-sm text-[#4B5773] mt-0.5">
            Target vs Actual. See how each team member's results compare
            to the work they were assigned on the rota.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E5E7EB]">
        {[
          ["activities", "Activities"],
          ["enter", "Enter results"],
          ["report", "Report"],
          ["dashboard", "Dashboard"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`performance-tab-${key}`}
            onClick={() => setTab(key)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition "
              + (tab === key
                ? "border-[#0F1B3D] text-[#0F1B3D]"
                : "border-transparent text-[#4B5773] hover:text-[#0F1B3D]")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {/* --------- Activities tab --------- */}
      {tab === "activities" && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-[#0F1B3D]">
                Activities you're tracking
              </div>
              <div className="text-xs text-[#4B5773]">
                Each activity is linked to a rota category so you can compare
                results to the time your team spent on that work.
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="performance-pick-templates-btn"
                onClick={() => setTemplatePickerOpen(true)}
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Pick from templates
              </Button>
              <Button
                size="sm"
                data-testid="performance-new-activity-btn"
                onClick={() => setEditor({ open: true, editing: null })}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New activity
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-[#4B5773]">Loading…</div>
          ) : activitiesEnriched.length === 0 ? (
            <div
              data-testid="performance-empty-state"
              className="text-sm text-[#4B5773] py-8 text-center"
            >
              No activities yet. Click <span className="font-medium">New activity</span>
              {" "}or <span className="font-medium">Pick from templates</span> to start.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="performance-activities-table">
              <thead>
                <tr className="text-left text-[11px] text-[#4B5773] uppercase tracking-wide">
                  <th className="py-1.5">Activity</th>
                  <th className="py-1.5">Linked to</th>
                  <th className="py-1.5">Target</th>
                  <th className="py-1.5">Basis</th>
                  <th className="py-1.5">Period</th>
                  <th className="py-1.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activitiesEnriched.map((a) => {
                  // Phase 2Z.an.2 — Selected-teams-only activities
                  // signal "set up" via the presence of >=1 active
                  // team target instead of the base target_value
                  // (which is intentionally null in that mode).
                  const isSelectedTeamsOnly =
                    a.target_value == null
                    && (a.team_override_count || 0) > 0;
                  const needsLink = !a.linked_category_id;
                  const needsTarget = isSelectedTeamsOnly
                    ? false
                    : a.target_value == null;
                  const needsSetup = needsLink || needsTarget;
                  return (
                    <tr
                      key={a.kpi_id}
                      className="border-t border-[#F1F2F6]"
                      data-testid={`performance-activity-row-${a.kpi_id}`}
                    >
                      <td className="py-2 font-medium">
                        {a.name}
                        {needsSetup && (
                          <span
                            data-testid={`performance-activity-needs-setup-${a.kpi_id}`}
                            className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#9A3412] border border-[#FED7AA] align-middle"
                          >
                            Needs setup
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-[#4B5773]">
                        {a.linked_category_name || (
                          <button
                            type="button"
                            className="underline text-[#1D4ED8] text-xs"
                            onClick={() => setEditor({ open: true, editing: a })}
                            data-testid={`performance-activity-add-link-${a.kpi_id}`}
                          >
                            Add link
                          </button>
                        )}
                      </td>
                      <td className="py-2">
                        {a.target_value != null ? (
                          <>{a.target_value} {a.unit}</>
                        ) : isSelectedTeamsOnly ? (
                          <span
                            className="text-[#4B5773] text-xs"
                            data-testid={`performance-activity-team-targets-${a.kpi_id}`}
                          >
                            {a.team_override_count} team target{a.team_override_count === 1 ? "" : "s"}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="underline text-[#1D4ED8] text-xs"
                            onClick={() => setEditor({ open: true, editing: a })}
                            data-testid={`performance-activity-add-target-${a.kpi_id}`}
                          >
                            Add target
                          </button>
                        )}
                      </td>
                      <td className="py-2 text-[#4B5773]">
                        {(a.target_basis || "per_employee").replace("_", " ")}
                      </td>
                      <td className="py-2 text-[#4B5773]">{a.period || "—"}</td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`performance-activity-edit-${a.kpi_id}`}
                          onClick={() => setEditor({ open: true, editing: a })}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`performance-activity-delete-${a.kpi_id}`}
                          onClick={async () => {
                            // Phase 2Z.s-2 — friendly archive wording
                            // when historical results exist. The
                            // backend hard-deletes empty activities
                            // and archives ones with results; the
                            // UI confirmation mirrors that intent.
                            // We can't know "has values" without
                            // a round-trip, so we use a single
                            // safe confirm and let the backend pick.
                            const msg = (
                              `Delete "${a.name}"?\n\n`
                              + "If this activity has saved results, "
                              + "we'll archive it so historical reports stay intact. "
                              + "If it has no results yet, it will be removed."
                            );
                            if (!window.confirm(msg)) return;
                            try {
                              const { data } = await api.delete(`/kpis/${a.kpi_id}`);
                              if (data?.archived) {
                                toast.success(
                                  "Activity archived. Historical reports kept.");
                              } else {
                                toast.success("Activity removed");
                              }
                              loadAll();
                            } catch (err) {
                              toast.error(
                                formatError(err.response?.data?.detail) || "Delete failed"
                              );
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* --------- Enter results tab --------- */}
      {tab === "enter" && (
        <Card>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">Activity</label>
              <Select value={selectedActivityId} onValueChange={setSelectedActivityId}>
                <SelectTrigger
                  className="w-[260px]"
                  data-testid="performance-grid-activity-select"
                >
                  <SelectValue placeholder="Choose an activity" />
                </SelectTrigger>
                <SelectContent>
                  {activitiesEnriched.map((a) => (
                    <SelectItem key={a.kpi_id} value={a.kpi_id}>
                      {a.name} — {a.linked_category_name || "no link"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">From</label>
              <Input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[150px]"
                data-testid="performance-grid-from-date"
              />
            </div>
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">To</label>
              <Input
                type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[150px]"
                data-testid="performance-grid-to-date"
              />
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!selectedActivity}
                onClick={() => setImportDialog({
                  open: true, activity: selectedActivity,
                })}
                data-testid="performance-import-results-btn"
              >
                Import results
              </Button>
              <Button
                size="sm"
                disabled={!selectedActivity || !isDirty}
                onClick={saveAll}
                data-testid="performance-grid-save-btn"
              >
                Save all changes
              </Button>
            </div>
          </div>

          {!selectedActivity ? (
            <div className="text-sm text-[#4B5773] py-6 text-center">
              Pick an activity to start entering results.
            </div>
          ) : (
            <>
              {/* Phase 2Z.s-3 — friendly overlap banner. If any
                  saved value partially overlaps the selected window
                  (but doesn't match it exactly), surface the most
                  common saved range with two single-click actions:
                  jump to that range OR continue and let the save
                  flow show the 409 Replace confirm. The grid stays
                  empty for non-exact overlaps so the manager never
                  mistakes a partial value for a full one. */}
              {(() => {
                const periodsCount = {};
                Object.values(overlappingByEmp).forEach((arr) => {
                  arr.forEach((row) => {
                    const key = `${row.period_start}|${row.period_end}`;
                    periodsCount[key] = (periodsCount[key] || 0) + 1;
                  });
                });
                const entries = Object.entries(periodsCount);
                if (entries.length === 0) return null;
                entries.sort((a, b) => b[1] - a[1]);
                const [topKey, topCount] = entries[0];
                const [topStart, topEnd] = topKey.split("|");
                const empCount = Object.keys(overlappingByEmp).length;
                const useThatPeriod = () => {
                  setFromDate(topStart);
                  setToDate(topEnd);
                };
                return (
                  <div
                    data-testid="performance-grid-overlap-banner"
                    className="mb-3 p-3 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] flex items-start gap-3"
                  >
                    <div className="flex-1 text-sm text-[#9A3412]">
                      <div className="font-medium">
                        Results already exist for this activity covering{" "}
                        {topStart} to {topEnd}.
                      </div>
                      <div className="text-xs text-[#9A3412]/85 mt-0.5">
                        Use that saved period, replace it on save, or choose
                        another range. {topCount} entr{topCount === 1 ? "y" : "ies"}
                        {entries.length > 1
                          ? ` (plus ${entries.length - 1} more overlapping range${entries.length - 1 === 1 ? "" : "s"})`
                          : ""}{" "}
                        across {empCount} employee{empCount === 1 ? "" : "s"}.
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="performance-grid-overlap-use-saved-btn"
                        onClick={useThatPeriod}
                      >
                        Use saved period
                      </Button>
                    </div>
                  </div>
                );
              })()}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="performance-grid-table">
                <thead>
                  <tr className="text-left text-[11px] text-[#4B5773] uppercase tracking-wide">
                    <th className="py-1.5">Employee</th>
                    <th className="py-1.5">
                      Assigned on {selectedActivity.linked_category_name || "—"}
                    </th>
                    <th className="py-1.5">Expected for this window</th>
                    <th className="py-1.5">Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveEmployees.map((e) => {
                    const row = actuals[e.employee_id] || { current: null };
                    // Per-row expected: prefer the snapshot (historical
                    // safety) ONLY when valid + positive. Fall back to
                    // the live computed expected so the cell never
                    // shows a misleading 0 for activities whose saved
                    // snapshot is missing target_value/frequency.
                    // Phase 2Z.an.2 — Selected-teams-only fallback
                    // uses the employee's team override.
                    let liveExpected = _performanceExpectedForWindow(
                      selectedActivity.target_value,
                      selectedActivity.target_frequency,
                      fromDate, toDate,
                      selectedActivity.target_days);
                    if (selectedActivity.target_value == null
                        && (selectedActivity.applicable_team_ids || []).length > 0) {
                      const ov = (selectedActivity.team_overrides || [])
                        .find((o) => o.team_id === e.team_id);
                      if (ov && ov.target_value != null) {
                        liveExpected = _performanceExpectedForWindow(
                          ov.target_value,
                          ov.target_frequency || selectedActivity.target_frequency,
                          fromDate, toDate,
                          (ov.target_days && ov.target_days.length)
                            ? ov.target_days
                            : selectedActivity.target_days);
                      } else {
                        liveExpected = null;
                      }
                    }
                    const snap = row.target_snapshot;
                    const snapVal = snap && snap.expected_for_window;
                    const snapValid = (typeof snapVal === "number"
                      && isFinite(snapVal) && snapVal > 0);
                    const expected = snapValid ? snapVal : liveExpected;
                    return (
                      <tr key={e.employee_id} className="border-t border-[#F1F2F6]">
                        <td className="py-2">{e.name}</td>
                        <td className="py-2 text-[#4B5773]">
                          {fmtHours(assignedByEmp[e.employee_id])}
                        </td>
                        <td
                          className="py-2 text-[#4B5773]"
                          data-testid={`performance-grid-expected-${e.employee_id}`}
                          title={snapValid
                            ? "Target as it was when the result was saved."
                            : undefined}
                        >
                          {expected == null ? (
                            <span className="italic text-[#9CA3AF]">No target set</span>
                          ) : (
                            Number.isInteger(expected) ? expected
                              : expected.toFixed(1)
                          )}
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={row.current ?? ""}
                            onChange={(ev) => setActuals((s) => ({
                              ...s,
                              [e.employee_id]: {
                                ...s[e.employee_id],
                                current: ev.target.value,
                              },
                            }))}
                            className="w-[110px] h-8"
                            data-testid={`performance-grid-actual-${e.employee_id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
          {reportLoading && (
            <div className="text-xs text-[#4B5773] mt-2">Loading…</div>
          )}
        </Card>
      )}

      {/* --------- Report tab --------- */}
      {tab === "report" && (
        <Card>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">Activity</label>
              <Select value={selectedActivityId} onValueChange={setSelectedActivityId}>
                <SelectTrigger
                  className="w-[260px]"
                  data-testid="performance-report-activity-select"
                >
                  <SelectValue placeholder="Choose an activity" />
                </SelectTrigger>
                <SelectContent>
                  {activitiesEnriched.map((a) => (
                    <SelectItem key={a.kpi_id} value={a.kpi_id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">From</label>
              <Input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[150px]"
                data-testid="performance-report-from-date"
              />
            </div>
            <div>
              <label className="text-xs text-[#4B5773] block mb-1">To</label>
              <Input
                type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[150px]"
                data-testid="performance-report-to-date"
              />
            </div>
          </div>

          {!selectedActivity ? (
            <div className="text-sm text-[#4B5773] py-6 text-center">
              Pick an activity to view the report.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="performance-report-table">
                  <thead>
                    <tr className="text-left text-[11px] text-[#4B5773] uppercase tracking-wide">
                      <th className="py-1.5">Employee</th>
                      <th className="py-1.5">
                        On {selectedActivity.linked_category_name || "—"}
                      </th>
                      <th className="py-1.5">Expected for this window</th>
                      <th className="py-1.5">Actual</th>
                      <th className="py-1.5">Gap</th>
                      <th className="py-1.5">Achievement</th>
                      <th className="py-1.5">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((r) => (
                      <tr key={r.employee_id} className="border-t border-[#F1F2F6]">
                        <td className="py-2">{r.name}</td>
                        <td className="py-2 text-[#4B5773]">
                          {fmtHours(r.assigned_mins)}
                        </td>
                        <td
                          className="py-2 text-[#4B5773]"
                          data-testid={`performance-report-expected-${r.employee_id}`}
                          title={
                            r.fully_unavailable
                              ? "No available working time in this period."
                              : r.adjusted_by_leave
                              ? "Expected target reduced because this employee had leave / bank holiday time in the selected period."
                              : r.from_snapshot
                              ? "Target as it was when the result was saved."
                              : undefined
                          }
                        >
                          {r.fully_unavailable ? (
                            <span
                              className="italic text-[#9CA3AF]"
                              data-testid={`performance-report-no-avail-${r.employee_id}`}
                            >No available time</span>
                          ) : r.target == null ? (
                            <span className="italic text-[#9CA3AF]">No target set</span>
                          ) : (
                            Number.isInteger(r.target) ? r.target
                              : r.target.toFixed(1)
                          )}
                          {r.adjusted_by_leave && !r.fully_unavailable && (
                            <span
                              className="ml-1.5 text-[10px] text-[#9A3412] bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-1.5 py-0.5"
                              data-testid={`performance-report-adjusted-${r.employee_id}`}
                              aria-label="Adjusted for leave or bank holiday"
                            >adj.</span>
                          )}
                          {r.from_snapshot && (
                            <span
                              data-testid={`performance-report-snapshot-${r.employee_id}`}
                              aria-label="Snapshot from when the result was saved"
                              className="sr-only"
                            >
                              saved target
                            </span>
                          )}
                        </td>
                        <td className="py-2 font-medium">
                          {r.actual ?? "—"}
                        </td>
                        <td
                          className={
                            r.gap == null ? "py-2 text-[#4B5773]"
                            : r.gap >= 0 ? "py-2 text-[#16A34A]"
                            : "py-2 text-[#EA580C]"
                          }
                        >
                          {r.gap == null ? "—" : (r.gap > 0 ? `+${r.gap}` : r.gap)}
                        </td>
                        <td className="py-2 text-[#4B5773]">
                          {r.fully_unavailable ? "—" : fmtPct(r.achievement_pct)}
                        </td>
                        <td className="py-2">{r.fully_unavailable ? "—" : dots(r.score)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {teamRollup && (
                    <tfoot>
                      <tr className="border-t-2 border-[#E5E7EB] font-semibold">
                        <td className="py-2">Team summary</td>
                        <td className="py-2 text-[#4B5773]">
                          {fmtHours(teamRollup.assigned_mins)}
                        </td>
                        <td className="py-2">
                          {teamRollup.target == null ? "—" : (
                            Number.isInteger(teamRollup.target) ? teamRollup.target
                              : teamRollup.target.toFixed(1)
                          )}
                        </td>
                        <td className="py-2">{teamRollup.actual}</td>
                        <td
                          className={
                            teamRollup.gap == null ? ""
                            : teamRollup.gap >= 0 ? "text-[#16A34A]"
                            : "text-[#EA580C]"
                          }
                        >
                          {teamRollup.gap == null ? "—"
                            : (teamRollup.gap > 0 ? `+${teamRollup.gap}` : teamRollup.gap)}
                        </td>
                        <td className="py-2">{fmtPct(teamRollup.achievement_pct)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {teamRollup?.under_target && (
                <div
                  data-testid="performance-suggest-rota-focus-card"
                  className="mt-4 p-3 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] flex items-start gap-3"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#9A3412]">
                      {selectedActivity.linked_category_name || selectedActivity.name}
                      {" "}is below target this period.
                    </div>
                    <div className="text-xs text-[#9A3412]/80 mt-0.5">
                      Open RMI with a safe rebalance suggestion already
                      prefilled. Preview before applying. Nothing changes
                      automatically.
                    </div>
                  </div>
                  <Button
                    size="sm"
                    data-testid="performance-suggest-rota-focus-btn"
                    onClick={() => suggestRotaFocus(selectedActivity)}
                  >
                    Suggest rota focus
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* --------- Phase 2Z.u — Dashboard tab --------- */}
      {tab === "dashboard" && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-sm font-semibold text-[#0F1B3D]">
                Performance dashboard
              </div>
              <div className="text-xs text-[#4B5773]">
                Achievement, gaps and linked-category time at a glance.
                Pick a scope and a period to see the picture.
              </div>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                  From
                </label>
                <Input
                  type="date" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="performance-dashboard-from"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                  To
                </label>
                <Input
                  type="date" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="performance-dashboard-to"
                />
              </div>
            </div>
          </div>

          {/* Scope selector */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Button
              variant={dashScope === "company" ? "default" : "outline"}
              size="sm"
              data-testid="performance-dashboard-scope-company"
              onClick={() => { setDashScope("company"); setDashScopeId(""); }}
            >Company</Button>
            <Button
              variant={dashScope === "team" ? "default" : "outline"}
              size="sm"
              data-testid="performance-dashboard-scope-team"
              onClick={() => { setDashScope("team"); setDashScopeId(""); }}
            >Team</Button>
            <Button
              variant={dashScope === "employee" ? "default" : "outline"}
              size="sm"
              data-testid="performance-dashboard-scope-employee"
              onClick={() => { setDashScope("employee"); setDashScopeId(""); }}
            >Employee</Button>
            {dashScope === "team" && (
              <Select value={dashScopeId} onValueChange={setDashScopeId}>
                <SelectTrigger
                  className="w-[240px] h-8 text-sm"
                  data-testid="performance-dashboard-team-select"
                ><SelectValue placeholder="Pick a team" /></SelectTrigger>
                <SelectContent>
                  {dashTeams.map((t) => (
                    <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {dashScope === "employee" && (
              <Select value={dashScopeId} onValueChange={setDashScopeId}>
                <SelectTrigger
                  className="w-[240px] h-8 text-sm"
                  data-testid="performance-dashboard-employee-select"
                ><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {dashLoading && (
            <div className="text-sm text-[#4B5773] py-12 text-center"
              data-testid="performance-dashboard-loading">
              Loading dashboard…
            </div>
          )}

          {!dashLoading && dashScope !== "company" && !dashScopeId && (
            <div className="text-sm text-[#4B5773] py-12 text-center">
              Pick a {dashScope} above to see results.
            </div>
          )}

          {!dashLoading && dashData && (
            <>
              {/* Phase 2Z.u-2 — Availability-adjustment banner. Only
                  shown when leave / bank holiday actually reduced
                  the expected target so managers know the numbers
                  are fair, not just incorrect. */}
              {dashData.summary.availability_adjusted && (
                <div
                  className="mb-3 rounded-md border border-[#FED7AA] bg-[#FFF7ED] px-3 py-2 text-xs text-[#9A3412]"
                  data-testid="performance-dashboard-availability-banner"
                >
                  {dashData.summary.availability_note}
                  {dashData.summary.fully_unavailable_count > 0 && (
                    <span className="ml-1">
                      {dashData.summary.fully_unavailable_count} employee{
                        dashData.summary.fully_unavailable_count === 1 ? "" : "s"
                      } had no available working time and {
                        dashData.summary.fully_unavailable_count === 1 ? "is" : "are"
                      } not scored for this period.
                    </span>
                  )}
                </div>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div
                  className="rounded-xl border border-[#E5E7EB] bg-white p-3"
                  data-testid="performance-dashboard-card-achievement"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                    Overall achievement
                  </div>
                  <div className="text-2xl font-semibold text-[#0F1B3D] mt-1">
                    {fmtPct(dashData.summary.overall_achievement_pct)}
                  </div>
                  <div className="text-[11px] text-[#4B5773]">
                    {dashData.summary.employee_count} employee{
                      dashData.summary.employee_count === 1 ? "" : "s"} in scope
                  </div>
                </div>
                <div
                  className="rounded-xl border border-[#E5E7EB] bg-white p-3"
                  data-testid="performance-dashboard-card-activities-rag"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                    Activities
                  </div>
                  <div className="text-2xl font-semibold text-[#0F1B3D] mt-1">
                    {dashData.summary.activities_total}
                  </div>
                  <div className="text-[11px] mt-0.5 flex gap-2">
                    <span className="text-[#16A34A]">
                      ● {dashData.summary.activities_green}
                    </span>
                    <span className="text-[#F59E0B]">
                      ● {dashData.summary.activities_amber}
                    </span>
                    <span className="text-[#DC2626]">
                      ● {dashData.summary.activities_red}
                    </span>
                  </div>
                </div>
                <div
                  className="rounded-xl border border-[#E5E7EB] bg-white p-3"
                  data-testid="performance-dashboard-card-leave"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                    Leave + Bank holidays
                  </div>
                  <div className="text-2xl font-semibold text-[#0F1B3D] mt-1">
                    {dashData.summary.leave_hours} h
                  </div>
                  <div className="text-[11px] text-[#4B5773]">
                    incl. {dashData.summary.bank_holiday_hours} h bank holidays
                  </div>
                </div>
                <div
                  className="rounded-xl border border-[#E5E7EB] bg-white p-3"
                  data-testid="performance-dashboard-card-delta"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                    Δ vs previous period
                  </div>
                  {(() => {
                    // Phase 2Z.u-1: when prev is null, the previous
                    // window has no comparable data — never show a
                    // misleading "+100 pp" delta.
                    const prev = dashData.summary.overall_achievement_pct_previous;
                    const d = _dashboardComputeDelta(
                      dashData.summary.overall_achievement_pct, prev);
                    if (d == null) {
                      return (
                        <>
                          <div className="text-2xl font-semibold text-[#94A3B8] mt-1">
                            —
                          </div>
                          <div className="text-[11px] text-[#4B5773]">
                            No previous data
                          </div>
                        </>
                      );
                    }
                    const cls = d > 0 ? "text-[#16A34A]"
                      : d < 0 ? "text-[#DC2626]" : "text-[#0F1B3D]";
                    return (
                      <>
                        <div className={`text-2xl font-semibold mt-1 ${cls}`}>
                          {d > 0 ? `+${d}` : d} pp
                        </div>
                        <div className="text-[11px] text-[#4B5773]">
                          {dashData.period.previous_from} → {dashData.period.previous_to}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Activities table */}
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="performance-dashboard-activities-table"
                >
                  <thead>
                    <tr className="text-left text-[#4B5773] text-xs uppercase tracking-widest">
                      <th className="py-2">Activity</th>
                      <th className="py-2">Linked time</th>
                      <th className="py-2">Target</th>
                      <th className="py-2">Actual</th>
                      <th className="py-2">Gap</th>
                      <th className="py-2">Ach %</th>
                      <th className="py-2">Δ</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashData.activities.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-4 text-[#4B5773] italic">
                          No Performance Focus activities yet. Use the Activities tab to add one.
                        </td>
                      </tr>
                    )}
                    {dashData.activities.map((a) => {
                      const status = a.status || _dashboardClassifyStatus(a.achievement_pct);
                      const statusColor = status === "green" ? "#16A34A"
                        : status === "amber" ? "#F59E0B"
                        : status === "red" ? "#DC2626" : "#94A3B8";
                      const delta = a.delta_pct;
                      const deltaCls = delta == null ? "text-[#94A3B8]"
                        : delta > 0 ? "text-[#16A34A]"
                        : delta < 0 ? "text-[#DC2626]" : "text-[#4B5773]";
                      return (
                        <tr
                          key={a.kpi_id}
                          className="border-t border-[#F1F2F6]"
                          data-testid={`performance-dashboard-activity-row-${a.kpi_id}`}
                        >
                          <td className="py-2">
                            <div className="font-medium text-[#0F1B3D] flex items-center gap-2 flex-wrap">
                              <span>{a.name}</span>
                              {/* Phase 2Z.an — Selected-teams-only chip.
                                  Tells the manager at a glance that this
                                  activity is intentionally restricted. */}
                              {a.applicability === "selected_teams_only" && (
                                <span
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-medium"
                                  data-testid={`performance-dashboard-activity-applicability-${a.kpi_id}`}
                                  title="Only the selected teams are measured on this activity."
                                >
                                  Selected teams only
                                </span>
                              )}
                              {/* Phase 2Z.am — team override count chip. */}
                              {(a.team_override_count || 0) > 0 && (
                                <span
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8] font-medium"
                                  data-testid={`performance-dashboard-activity-overrides-${a.kpi_id}`}
                                  title="This activity has team-specific targets. Teams without an override use the base target."
                                >
                                  {a.team_override_count} team target{a.team_override_count === 1 ? "" : "s"}
                                </span>
                              )}
                            </div>
                            {a.linked_category_name && (
                              <div className="text-[11px] text-[#4B5773]">
                                Linked to {a.linked_category_name}
                              </div>
                            )}
                          </td>
                          <td className="py-2 text-[#4B5773]">
                            {a.linked_category_hours} h
                          </td>
                          <td className="py-2">
                            {a.target == null ? "—"
                              : Number.isInteger(a.target) ? a.target
                              : a.target.toFixed(1)}
                          </td>
                          <td className="py-2 font-medium">
                            {a.actual == null ? "—"
                              : Number.isInteger(a.actual) ? a.actual
                              : a.actual.toFixed(1)}
                          </td>
                          <td className={a.gap == null ? "py-2 text-[#4B5773]"
                            : a.gap >= 0 ? "py-2 text-[#16A34A]"
                            : "py-2 text-[#DC2626]"}>
                            {a.gap == null ? "—"
                              : (a.gap > 0 ? "+" : "")
                                + (Number.isInteger(a.gap) ? a.gap : a.gap.toFixed(1))}
                          </td>
                          <td className="py-2">{fmtPct(a.achievement_pct)}</td>
                          <td className={`py-2 ${deltaCls}`}>
                            {delta == null ? "—" : (delta > 0 ? `+${delta}` : delta)}
                          </td>
                          <td className="py-2">
                            <span
                              className="inline-flex items-center gap-1.5 text-xs"
                              style={{ color: statusColor }}
                            >
                              <span
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ backgroundColor: statusColor }}
                              />
                              {status === "no_data" ? "No data"
                                : status[0].toUpperCase() + status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Top / Needs-attention panels. Hidden when scoped to
                  a single employee (showing the same person as both
                  top and bottom of themselves is confusing). */}
              {dashScope !== "employee"
                && (dashData.top_performers.length > 0
                    || dashData.needs_attention.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                  <div
                    className="rounded-xl border border-[#D1FAE5] bg-[#ECFDF5] p-3"
                    data-testid="performance-dashboard-top-performers"
                  >
                    <div className="text-xs font-semibold text-[#065F46] uppercase tracking-widest">
                      Top performers
                    </div>
                    <div className="text-[10px] text-[#065F46]/70 mt-0.5">
                      At or above 80% achievement
                    </div>
                    {dashData.top_performers.length === 0 && (
                      <div className="text-xs text-[#4B5773] mt-1.5 italic">
                        No-one is hitting 80% yet for this period.
                      </div>
                    )}
                    <ul className="mt-1.5 text-sm space-y-0.5">
                      {dashData.top_performers.map((p) => (
                        <li key={p.employee_id} className="flex justify-between">
                          <span className="text-[#0F1B3D]">{p.name}</span>
                          <span className="font-medium text-[#16A34A]">
                            {fmtPct(p.achievement_pct)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className="rounded-xl border border-[#FED7AA] bg-[#FFF7ED] p-3"
                    data-testid="performance-dashboard-needs-attention"
                  >
                    <div className="text-xs font-semibold text-[#9A3412] uppercase tracking-widest">
                      Needs attention
                    </div>
                    <div className="text-[10px] text-[#9A3412]/70 mt-0.5">
                      Below 80% achievement
                    </div>
                    {dashData.needs_attention.length === 0 && (
                      <div className="text-xs text-[#4B5773] mt-1.5 italic">
                        Nothing to flag. Everyone is at 80% or above.
                      </div>
                    )}
                    <ul className="mt-1.5 text-sm space-y-0.5">
                      {dashData.needs_attention.map((p) => (
                        <li key={p.employee_id} className="flex justify-between">
                          <span className="text-[#0F1B3D]">{p.name}</span>
                          <span className="font-medium text-[#DC2626]">
                            {fmtPct(p.achievement_pct)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Phase 2Z.u-1 — Selected employee summary card.
                  Replaces the top/needs-attention panels when the
                  dashboard is filtered to a single employee. */}
              {dashScope === "employee" && (
                <div
                  className="rounded-xl border border-[#E5E7EB] bg-white p-3 mt-5"
                  data-testid="performance-dashboard-employee-summary"
                >
                  <div className="text-xs font-semibold text-[#0F1B3D] uppercase tracking-widest mb-2">
                    Selected employee summary — {dashData.scope.name}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase text-[#94A3B8] font-semibold">
                        Achievement
                      </div>
                      <div className="text-lg font-semibold text-[#0F1B3D]">
                        {fmtPct(dashData.summary.overall_achievement_pct)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[#94A3B8] font-semibold">
                        Gap
                      </div>
                      <div className="text-lg font-semibold text-[#0F1B3D]">
                        {(() => {
                          const g = dashData.activities.reduce(
                            (acc, a) => acc + (a.gap || 0), 0);
                          if (!isFinite(g) || g === 0) return "0";
                          return (g > 0 ? "+" : "")
                            + (Number.isInteger(g) ? g : g.toFixed(1));
                        })()}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[#94A3B8] font-semibold">
                        Linked category hours
                      </div>
                      <div className="text-lg font-semibold text-[#0F1B3D]">
                        {dashData.activities.reduce(
                          (acc, a) => acc + (a.linked_category_hours || 0), 0
                        ).toFixed(1)} h
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[#94A3B8] font-semibold">
                        Status
                      </div>
                      {(() => {
                        const s = _dashboardClassifyStatus(
                          dashData.summary.overall_achievement_pct);
                        const c = s === "green" ? "#16A34A"
                          : s === "amber" ? "#F59E0B"
                          : s === "red" ? "#DC2626" : "#94A3B8";
                        return (
                          <div
                            className="text-lg font-semibold inline-flex items-center gap-1.5"
                            style={{ color: c }}
                          >
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: c }}
                            />
                            {s === "no_data" ? "No data"
                              : s[0].toUpperCase() + s.slice(1)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* --------- Editor dialog (reused from legacy KPI page) --------- */}
      {editor.open && (
        <KPIEditorDialog
          open={editor.open}
          mode="performance"
          onOpenChange={(o) => !o && setEditor({ open: false, editing: null })}
          editing={editor.editing}
          categories={categories}
          teams={dashTeams}
          employees={[]}
          onSaved={() => { setEditor({ open: false, editing: null }); loadAll(); }}
        />
      )}

      {/* --------- Template picker (filtered) --------- */}
      {templatePickerOpen && (
        <KPITemplatePicker
          open={templatePickerOpen}
          mode="performance"
          onOpenChange={(o) => !o && setTemplatePickerOpen(false)}
          hiddenKeys={PERFORMANCE_HIDDEN_TEMPLATE_KEYS}
          existingKeys={activities.map((a) => a.key).filter(Boolean)}
          onCreated={() => { setTemplatePickerOpen(false); loadAll(); }}
        />
      )}

      {/* --------- Import results dialog (Phase 2Z.s-5) --------- */}
      {importDialog.open && (
        <ImportResultsDialog
          open={importDialog.open}
          onOpenChange={(o) => !o && setImportDialog({ open: false, activity: null })}
          activity={importDialog.activity}
          employees={employees}
          onCommitted={() => { loadGrid(); }}
        />
      )}
    </div>
  );
}
